import type {
  TourOperatorRow,
  TourOperatorType,
} from '@ops/entities/operation-item';
import {
  useSetTourOperators,
  useTourOperatorsData,
} from '@ops/entities/operation-item';
import { TourOperatorForm } from '@ops/features/tour-operator-form';
import {
  backendMessageOr,
  toastMutation,
} from '@ops/shared/lib/toast-mutation';
import { Pencil, Star, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { joinCss } from '@/shared/lib/css-utils';
import { ServerSearchSelect, Tooltip } from '@/shared/ui';
import css from './operation-card-operator-picker.module.scss';

type OperationCardOperatorPickerProps = {
  itemId: string;
  // Resolved from the item (Driver for eligible transport, Guide for guides).
  operatorType: TourOperatorType;
  // Currently-assigned operator ids — used to build the wholesale write and to
  // hide already-assigned operators from the results.
  assignedIds: string[];
  // Item time window — operators already booked in it come back as busy
  // (`isAvailable === false`) and are disabled, mirroring legacy.
  startTime?: string | null;
  endTime?: string | null;
};

const SEARCH_LIMIT = 50;

// receiveMessage → type-letter colour + tooltip, mirroring legacy's
// RequestSearchOnSelectUpdate (green allowed / amber only-preparation / red
// not-allowed / muted unknown).
const RECEIVE_MESSAGE_META: Record<string, { cls: string; tip: string }> = {
  allowed: { cls: 'rmAllowed', tip: 'Message can be sent' },
  only_preparation: {
    cls: 'rmPrep',
    tip: 'Only preparation message can be sent',
  },
  not_allowed: { cls: 'rmBlocked', tip: 'Message cannot be sent' },
};

// "Add Driver" / "Add Guide" picker for the operations footer. Mirrors legacy's
// search-on-select control: searches the org catalog filtered to the item's
// operator type, then appends the pick to the per-item assignment via
// `setTourOperators` (wholesale id list — same write the footer "remove" uses).
export const OperationCardOperatorPicker = ({
  itemId,
  operatorType,
  assignedIds,
  startTime,
  endTime,
}: OperationCardOperatorPickerProps) => {
  const setTourOperators = useSetTourOperators();
  const [editingOperator, setEditingOperator] =
    useState<TourOperatorRow | null>(null);

  // Defined inline so the search closes over this item's operator type +
  // assigned ids (ServerSearchSelect's `useSearch(query)` contract takes only
  // the query). Safe re: rules-of-hooks: ServerSearchSelect calls it exactly
  // once, unconditionally, every render, so the hook order it observes is
  // stable regardless of this function's identity.
  const useSearch = (query: string, open: boolean) => {
    const trimmed = query.trim();
    const result = useTourOperatorsData(
      {
        search: trimmed.length > 0 ? trimmed : undefined,
        type: [operatorType],
        startTime: startTime ?? undefined,
        endTime: endTime ?? undefined,
        offset: 0,
        limit: SEARCH_LIMIT,
      },
      // Only fetch once the picker is actually opened — otherwise every card on
      // the operations page fires a windowed availability scan on mount.
      { enabled: open }
    );
    return {
      data: (result.data?.data ?? []).filter(
        (operator) => !assignedIds.includes(operator.id)
      ),
      isLoading: result.isLoading,
      // A failed lookup must not read as "this operator doesn't exist" — this
      // picker is the only caller that sends the item's time window, so it is
      // the one that can fail on the item's own data.
      isError: result.isError,
    };
  };

  const handleAssign = (operatorId: string | null) => {
    if (!operatorId || !itemId || assignedIds.includes(operatorId)) return;
    setTourOperators.mutate(
      {
        itineraryItemId: itemId,
        tourOperatorIds: [...assignedIds, operatorId],
      },
      toastMutation({
        success: `${operatorType} assigned`,
        // Surface the server's reason — "no valid international phone number
        // on the CRM record" names the operator and the fix; the generic line
        // leaves the consultant with nowhere to go.
        error: backendMessageOr(
          `Couldn’t assign ${operatorType.toLowerCase()}`
        ),
      })
    );
  };

  const renderOption = (operator: TourOperatorRow) => {
    const meta = RECEIVE_MESSAGE_META[operator.receiveMessage];
    const rating = operator.rating ? Number(operator.rating) : 0;
    return (
      <>
        <Tooltip content={meta?.tip ?? 'Messaging not configured'}>
          <span className={joinCss(css.typeLetter, meta && css[meta.cls])}>
            {operator.type === 'Driver' ? 'D' : 'G'}
          </span>
        </Tooltip>
        <span className={css.name}>{operator.fullName}</span>
        {rating > 0 && (
          <span className={css.rating}>
            {rating}
            <Star className={css.star} />
          </span>
        )}
        {operator.isAvailable === false && (
          <span className={css.assigned}>Assigned</span>
        )}
      </>
    );
  };

  return (
    <>
      <ServerSearchSelect<TourOperatorRow>
        appearance="button"
        disabled={!itemId}
        emptyMessage={`No ${operatorType.toLowerCase()}s found`}
        getOptionContent={renderOption}
        getOptionDisabled={(operator) => operator.isAvailable === false}
        getOptionLabel={(operator) => operator.fullName}
        getOptionTrailing={(operator) => (
          <button
            aria-label={`Edit ${operator.fullName}`}
            className={css.editButton}
            onClick={() => setEditingOperator(operator)}
            type="button"
          >
            <Pencil style={{ height: 13, width: 13 }} />
          </button>
        )}
        getOptionValue={(operator) => operator.id}
        leadingIcon={<UserPlus style={{ height: 12, width: 12 }} />}
        onChange={handleAssign}
        placeholder={`Add ${operatorType}`}
        searchPlaceholder={`Search ${operatorType.toLowerCase()}s…`}
        size="sm"
        useSearch={useSearch}
        usePortal
        value={null}
      />
      <TourOperatorForm
        initial={editingOperator}
        onClose={() => setEditingOperator(null)}
        open={editingOperator !== null}
      />
    </>
  );
};
